import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { CalendarClock } from "lucide-react";
import { Badge, Card, CardContent, EmptyState, PageLoading } from "@dosa/ui";
import { useAuth } from "../../hooks/useAuth";
import { listFollowUps } from "../../lib/api-client";

const statusVariant = { SCHEDULED: "info", COMPLETED: "success", MISSED: "high", CANCELLED: "neutral" } as const;

export function PatientFollowUpPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const patientId = user!.patientId!;

  const { data: followUps, isLoading } = useQuery({
    queryKey: ["patient-follow-ups", patientId],
    queryFn: () => listFollowUps(patientId),
  });

  if (isLoading) return <PageLoading />;

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold text-charcoal-900">{t("patient.followUp.title")}</h1>
      <Card>
        <CardContent className="p-0">
          {!followUps || followUps.length === 0 ? (
            <EmptyState className="border-0" title={t("patient.followUp.empty")} icon={<CalendarClock className="h-8 w-8" />} />
          ) : (
            <ul className="divide-y divide-charcoal-100">
              {followUps.map((f) => (
                <li key={f.id} className="flex items-center justify-between px-5 py-4">
                  <div>
                    <p className="font-medium text-charcoal-800">{t("patient.followUp.dueOn", { date: new Date(f.dueDate).toLocaleDateString() })}</p>
                    {f.reason && <p className="text-sm text-charcoal-400">{f.reason}</p>}
                  </div>
                  <Badge variant={statusVariant[f.status]}>{f.status}</Badge>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
